/**
 * Piezas de LA CONVERSACIÓN (Despacho → conversacion.html, validada
 * 2026-09-15). Envoltorios de system.css, uno por pieza; el contrato visual
 * es la canónica. Nada de CSS propio aquí ni en las páginas.
 */
import { Children, cloneElement, createContext, isValidElement, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type MouseEvent, type MutableRefObject, type ReactNode, type Ref, type TextareaHTMLAttributes } from "react";
import { fitTextarea } from "./autogrow";
import { cx } from "../cx";
import { Icon, type IconName } from "../icons/Icon";
import { Count } from "../primitives/Badge";
import { Skeleton } from "../patterns/Page";
import { devolverFoco, focables } from "./Cuenta";
import { FileChip, type Attachment } from "../house/House";

/** Regla de fecha que separa las jornadas de la conversación. */
export function DateRule({ children }: { children: ReactNode }) {
  return <div className="sk-daterule"><span>{children}</span></div>;
}

/** Prosa del asesor dentro de un turno (la de la card es `sk-card-prose`). */
export function TurnProse({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("sk-prose", className)} {...rest} />;
}

/**
 * Cifra en la frase: mono y tabular. En la frase, hasta tres o cuatro cifras;
 * a partir de ahí, una tabla (`ProseTable`) — revisión de la preferencia
 * «nunca en tabla», 2026-10-27: un ranking de diez alias en una frase se lee mal.
 */
export function Num({ children }: { children: ReactNode }) {
  return <span className="sk-num">{children}</span>;
}

/** La petición del asesor: pregunta + actos, resuelta cuando ya se respondió. */
/**
 * La petición es un GRUPO con nombre: su pregunta (`AskQ`) la titula
 * (`aria-labelledby`). Si lleva nota (`AskNote`, p. ej. «Sustituye a…»),
 * los botones de su `AskRow` la tienen como descripción: el lector la dice
 * al llegar al «sí», que es donde importa.
 */
interface AskIds { q: string; note: string; hasNote: boolean; setHasNote: (v: boolean) => void }
const AskCtx = createContext<AskIds | null>(null);
export function Ask({ settled, stale, compact, className, ...rest }: { settled?: boolean | undefined; stale?: boolean | undefined; compact?: boolean | undefined } & HTMLAttributes<HTMLDivElement>) {
  const base = useId();
  const [hasNote, setHasNote] = useState(false);
  const ids = { q: `${base}-q`, note: `${base}-note`, hasNote, setHasNote };
  return (
    <AskCtx.Provider value={ids}>
      <div role="group" aria-labelledby={ids.q} className={cx("sk-ask", settled && "sk-settled", compact && "sk-ask--compact", stale && "sk-stale", className)} {...rest} />
    </AskCtx.Provider>
  );
}
export function AskQ({ children }: { children: ReactNode }) {
  const ids = useContext(AskCtx);
  return <p className="sk-ask-q" {...(ids ? { id: ids.q } : {})}>{children}</p>;
}
/** La nota de la petición, entre la pregunta y los botones (susurro; el margen lo pone system.css). */
export function AskNote({ children }: { children: ReactNode }) {
  const ids = useContext(AskCtx);
  const set = ids?.setHasNote;
  useLayoutEffect(() => {
    set?.(true);
    return () => set?.(false);
  }, [set]);
  return <p className="sk-whisper" {...(ids ? { id: ids.note } : {})}>{children}</p>;
}
export function AskRow({ children }: { children: ReactNode }) {
  const ids = useContext(AskCtx);
  const described = ids?.hasNote === true ? ids.note : undefined;
  return (
    <div className="sk-ask-row">
      {described === undefined
        ? children
        : Children.map(children, (c) => (isValidElement<{ "aria-describedby"?: string }>(c) ? cloneElement(c, { "aria-describedby": described }) : c))}
    </div>
  );
}
/** El cierre de una petición: lo que quedó apuntado (o no). */
export function Settle({ children }: { children: ReactNode }) {
  return <p className="sk-settle">{children}</p>;
}

/**
 * La cola del asesor: lo demás espera a que tú quieras (R-3). El plural va
 * en la PIEZA, no en la página: con la cola real el 1 es alcanzable, y
 * «Tengo 1 temas más» es lo primero que se lee de una pantalla que promete
 * cuidado (la canónica pintaba un 2 fijo y no lo destapaba).
 */
export function QueueChip({ count, stale, ...rest }: { count: number; stale?: boolean | undefined } & HTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={cx("sk-queuechip", stale && "sk-stale")} {...rest}>
      Tengo <Count hot>{count}</Count> {count === 1 ? "tema más" : "temas más"} — cuando tú quieras
    </button>
  );
}

/** El asesor pensando, con la fase que de verdad está haciendo. */
export function Thinking({ phase, className }: { phase: ReactNode; className?: string | undefined }) {
  return (
    <div className={cx("sk-thinking", className)}>
      <span className="sk-think-dots"><i /><i /><i /></span>
      <span className="sk-think-phase">{phase}</span>
    </div>
  );
}

/**
 * Onda de voz. En el composer, al nivel del teclado mientras escucha (buey:
 * tu voz). `small` + `voice` = la onda en una línea de texto y en el acento
 * (oro en Despacho, azul en Aquila): la voz del asesor sonando (pie del
 * turno, 2026-09-29).
 */
export function Wave({ hidden, small, voice, levels, source }: {
  hidden?: boolean | undefined; small?: boolean | undefined; voice?: boolean | undefined;
  /** Niveles fijos (showcase, tests). */
  levels?: readonly number[] | null | undefined;
  /** Niveles EN VIVO: la onda se suscribe y escribe sus variables ella sola,
      sin pasar por el estado de React (a ~30 fps no se repinta la página). */
  source?: WaveLevelsSource | null | undefined;
}) {
  const live = levels != null && levels.length > 0;
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (source == null || el === null) return;
    const paint = (l: readonly number[] | null): void => {
      if (l === null || l.length === 0) {
        el.classList.remove("sk-wave--live");
        for (let i = 1; i <= 5; i++) el.style.removeProperty(`--sk-wave-l${i}`);
        return;
      }
      el.classList.add("sk-wave--live");
      for (const [k, v] of Object.entries(waveVars(l))) el.style.setProperty(k, String(v));
    };
    const off = source.subscribe(paint);
    return () => { off(); paint(null); };
  }, [source]);
  return (
    <span
      ref={ref}
      className={cx("sk-wave", small === true && "sk-wave--s", voice === true && "sk-wave--voice", live && "sk-wave--live")}
      style={live ? waveVars(levels) : undefined} hidden={hidden} aria-hidden="true"
    ><i /><i /><i /><i /><i /></span>
  );
}
/** De dónde lee la onda sus niveles en vivo (0–1 por barra; `null` = sin nivel). */
export interface WaveLevelsSource {
  subscribe: (cb: (levels: readonly number[] | null) => void) => () => void;
}
/** Suelo de la onda en vivo: en silencio respira, no parece muerta. */
export const WAVE_LIVE_FLOOR = 0.18;
/**
 * ONDA EN VIVO (2026-09-30): el nivel de cada barra (0–1) a las variables
 * `--sk-wave-l1…l5` que `sk-wave--live` usa como `scaleY`. Con suelo y techo,
 * y a dos decimales (no hace falta más para una barra de 1,3 rem).
 */
export function waveVars(levels: readonly number[]): CSSProperties {
  const vars: Record<string, string> = {};
  for (let i = 0; i < 5; i++) {
    const v = Math.min(1, Math.max(WAVE_LIVE_FLOOR, levels[i] ?? WAVE_LIVE_FLOOR));
    vars[`--sk-wave-l${i + 1}`] = String(Math.round(v * 100) / 100);
  }
  return vars as CSSProperties;
}

/** Pie del turno del asesor: la fila de actos bajo su prosa (hoy, escuchar). */
export function TurnFoot({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("sk-turn-foot", className)} {...rest} />;
}

export type ListenState = "idle" | "loading" | "playing" | "error";
export interface TurnListenProps {
  state: ListenState;
  /** Un solo mando: en reposo o en error, suena; preparando o sonando, para. */
  onToggle: () => void;
  /** Rótulos visibles (y nombre accesible: lo que se lee es lo que se dice). */
  labels?: Partial<Record<"idle" | "loading" | "playing", string>> | undefined;
  /** Lo que se dice cuando no ha podido sonar: honesto y corto. */
  error?: ReactNode | undefined;
  stale?: boolean | undefined;
}
const LISTEN_LABELS = { idle: "Escuchar", loading: "Preparando audio…", playing: "Parar" } as const;
const LISTEN_ERROR = "No he podido leerlo en voz alta. Vuelve a tocar Escuchar.";
/**
 * ESCUCHAR UNA RESPUESTA (▶/■), al pie del turno del asesor. Ghost S con
 * glifo + rótulo SIEMPRE visible: el porqué nunca vive solo en un tooltip.
 * El rótulo cambia con el estado y por eso NO lleva `aria-pressed` (un
 * conmutador con nombre cambiante se lee dos veces al revés); el estado lo
 * dicen el nombre y `aria-busy`. Preparando sigue siendo pulsable: para
 * (cancela la petición) — nada de mandos muertos mientras carga.
 * Preparando, el nombre dice lo que hace y la descripción (oculta a la
 * vista) lo que hace el clic: «Pulsa para cancelar».
 * En error vuelve a «Escuchar» (reintentar) con la frase al lado. Esa región
 * `role=status` está SIEMPRE montada (vacía y oculta a la vista mientras no
 * hay error): una región viva que nace con contenido no se anuncia.
 */
export function TurnListen({ state, onToggle, labels, error, stale }: TurnListenProps) {
  const errId = useId();
  const hintId = useId();
  const shown = state === "error" ? "idle" : state;
  const label = labels?.[shown] ?? LISTEN_LABELS[shown];
  return (
    <>
      <button
        type="button" className={cx("sk-btn", "sk-btn--ghost", "sk-btn--s", "sk-listen", stale === true && "sk-stale")}
        data-state={state}
        aria-busy={state === "loading" || undefined}
        aria-describedby={state === "error" ? errId : state === "loading" ? hintId : undefined}
        {...(stale === true && { "aria-disabled": true, tabIndex: -1 })}
        onClick={stale === true ? undefined : onToggle}
      >
        {state === "loading" ? <span className="sk-spinner" aria-hidden="true" /> : <Icon name={state === "playing" ? "stop" : "play"} />}
        <span>{label}</span>
        {state === "playing" && <Wave small voice />}
      </button>
      {/* fuera del botón: dentro sumaría al nombre accesible */}
      {state === "loading" && <span className="sk-vh" id={hintId}>Pulsa para cancelar</span>}
      <span className={cx("sk-error-msg", state !== "error" && "sk-vh")} id={errId} role="status">{state === "error" ? (error ?? LISTEN_ERROR) : null}</span>
    </>
  );
}

export interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  placeholder: string;
  /** Escuchando: la onda sustituye al silencio y el mando queda pulsado. */
  listening?: boolean | undefined;
  /** Abriendo el micro: tocaste, pero aún no graba (permiso, arranque del
      aparato). Spinner en el micro, «Abriendo el micrófono…» en la línea de
      estado y NADA de «te escucho»; el micro sigue pulsable para cancelar. */
  starting?: boolean | undefined;
  /** El texto de la línea de estado mientras se abre el micro. */
  startingLabel?: ReactNode | undefined;
  /** El nivel del micro, barra a barra (0–1), EN VIVO: la onda se suscribe
      y se mueve con tu voz sin repintar el composer ni la página. Sin
      niveles, la onda de siempre (animada, o quieta con reduced-motion). */
  waveLevelsSource?: WaveLevelsSource | null | undefined;
  /** Lo que oye el lector cuando la grabación empieza de verdad (oculto a la
      vista: la señal visible es el rótulo del campo y la onda). */
  listeningLabel?: string | undefined;
  onListen?: (() => void) | undefined;
  /** Voz deshabilitada CON PORQUÉ: sigue alcanzable a foco y hover, pero no
      hace nada, y el tooltip dice por qué (§5, visibilidad ≠ autorización). */
  voiceTip?: string | undefined;
  /** La glosa VISIBLE del porqué: el tooltip no se pinta por debajo de 560 px,
      así que el motivo nunca puede vivir solo en él (decisión de cuenta.html). */
  voiceDescribedBy?: string | undefined;
  /** Nota de voz en el servidor: el micro trabaja (spinner, `aria-busy`) y
      no se puede volver a pulsar; la línea de estado dice «Transcribiendo…». */
  transcribing?: boolean | undefined;
  /** El texto de la línea de estado mientras transcribe. */
  transcribingLabel?: ReactNode | undefined;
  /** La voz ha fallado (sin permiso, no se pudo transcribir…): frase corta y
      cómo arreglarlo, en la línea de estado, `role=alert`, y el micro la
      lleva en su `aria-describedby`. El micro sigue vivo para reintentar. */
  voiceError?: ReactNode | undefined;
  /** El glifo del error: `mic-off` solo cuando el micro no está a mano (sin
      permiso, sin micro); un fallo al transcribir no es culpa del micro. */
  voiceErrorIcon?: "mic-off" | "none" | undefined;
  /** El rótulo del campo mientras escucha (la señal en texto, no solo la onda). */
  listeningPlaceholder?: string | undefined;
  /** Adjuntar un documento: con esto se pinta el clip, entre el campo y el
      micro. Abrir el selector de ficheros es cosa de la página. */
  onAttach?: (() => void) | undefined;
  /** Qué se puede adjuntar («PDF con texto, Word o texto · hasta 20
      páginas»): tooltip del clip y, siempre, su descripción para el lector. */
  attachHint?: string | undefined;
  /** El documento elegido: una fila dentro de la caja, encima del campo.
      Leyendo, el clip y el envío esperan; se puede seguir escribiendo. */
  attachment?: Attachment | null | undefined;
  onRemoveAttachment?: (() => void) | undefined;
  /** El documento no se puede leer: NO deja ficha; la frase (qué pasa y qué
      hacer) va en la línea de estado, `role=alert`, y el clip la lleva en su
      `aria-describedby` y sigue vivo para probar con otro. */
  attachError?: ReactNode | undefined;
  /** Hay un fichero arrastrándose encima: la caja entera es la zona de soltar. */
  dragging?: boolean | undefined;
  /** Lo que dice la línea de estado mientras se arrastra. */
  draggingLabel?: ReactNode | undefined;
  /** Lo que oye el lector mientras se lee el documento y cuando ya está
      listo (por defecto, «Leyendo <nombre>…» y «<nombre>, <dato>. Listo
      para enviar.»). A la vista lo dice la ficha. */
  readingLabel?: string | undefined;
  attachedLabel?: string | undefined;
  disabled?: boolean | undefined;
  stale?: boolean | undefined;
  sending?: boolean | undefined;
  textareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement> | undefined;
  textareaRef?: Ref<HTMLTextAreaElement> | undefined;
}
/** El composer: teclado, voz y documento al mismo nivel, envío con Enter. */
export function Composer({ value, onChange, onSend, placeholder, listening, starting, startingLabel, waveLevelsSource, listeningLabel = "Te escucho. Habla cuando quieras.", onListen, voiceTip, voiceDescribedBy, transcribing, transcribingLabel, voiceError, voiceErrorIcon = "mic-off", listeningPlaceholder = "Te escucho — habla con normalidad…", onAttach, attachHint, attachment, onRemoveAttachment, attachError, dragging, draggingLabel = "Suelta aquí el documento.", readingLabel, attachedLabel, disabled, stale, sending, textareaProps, textareaRef }: ComposerProps) {
  const off = disabled === true || stale === true;
  const busy = transcribing === true;
  const opening = !busy && starting === true;
  const recording = listening === true && !busy && !opening;
  const file = attachment ?? null;
  const reading = file !== null && file.reading === true;
  // mientras se lee uno no se suelta otro: la caja no invita
  const dropping = dragging === true && !off && !reading;
  const status = busy ? (transcribingLabel ?? "Transcribiendo…") : opening ? (startingLabel ?? "Abriendo el micrófono…") : dropping ? draggingLabel : null;
  const failed = !busy && !opening && voiceError !== undefined && voiceError !== null && voiceError !== false;
  // lo que el lector oye del documento: que se lee y, al acabar, que ya se
  // puede enviar (el clip y el envío salen de su espera sin hacer ruido)
  const fileSaid = file === null ? null
    : reading ? (readingLabel ?? `Leyendo ${file.name}…`)
    : (attachedLabel ?? `${file.name}${typeof file.meta === "string" && file.meta ? `, ${file.meta}` : ""}. Listo para enviar.`);
  // la voz manda en la línea de error: su fallo es el más reciente que se oye
  const rejected = !failed && !reading && attachError !== undefined && attachError !== null && attachError !== false;
  const statusId = useId();
  const hintId = useId();
  const attachHintId = useId();
  const liveId = useId();
  // en espera CON porqué: leyendo, el clip y el envío llevan descrito lo que pasa
  const clipDescribedBy = [attachHint !== undefined ? attachHintId : undefined, rejected ? statusId : undefined, reading ? liveId : undefined].filter(Boolean).join(" ") || undefined;
  const send = () => { if (!reading) onSend(); };
  const describedBy = [voiceDescribedBy, failed ? statusId : undefined, opening ? hintId : undefined].filter(Boolean).join(" ") || undefined;
  const area = useRef<HTMLTextAreaElement | null>(null);
  const setArea = useCallback((el: HTMLTextAreaElement | null) => {
    area.current = el;
    if (typeof textareaRef === "function") textareaRef(el);
    else if (textareaRef != null) (textareaRef as MutableRefObject<HTMLTextAreaElement | null>).current = el;
  }, [textareaRef]);
  // crece con el texto (y encoge al vaciarse tras enviar), antes de pintar
  // …y con el rótulo de escucha, que es señal de estado y se lee entero
  useLayoutEffect(() => { if (area.current !== null) fitTextarea(area.current); }, [value, recording]);
  return (
    <div className={cx("sk-composer", (status !== null || failed || rejected) && "sk-composer--status", file !== null && "sk-composer--files", dropping && "sk-dragover", stale && "sk-stale")}>
      {/* las dos regiones vivas están SIEMPRE montadas (vacías y ocultas a
          la vista en reposo): una región que nace con contenido no se anuncia */}
      {/* grabando, la MISMA región dice «te escucho» solo al lector: a la
          vista ya lo dicen el rótulo del campo y la onda */}
      <p className={cx("sk-composer-status", status === null && "sk-vh")} id={liveId} role="status">{status ?? (recording ? listeningLabel : fileSaid)}</p>
      <p className={cx("sk-composer-status", "sk-error-msg", !failed && !rejected && "sk-vh")} id={statusId} role="alert">
        {failed ? <>{voiceErrorIcon === "mic-off" && <Icon name="mic-off" />}<span>{voiceError}</span></> : rejected ? <><Icon name="file" /><span>{attachError}</span></> : null}
      </p>
      {/* al quitar, el botón desaparece con el foco dentro: el foco vuelve al campo */}
      {file !== null && <div className="sk-composer-files"><FileChip {...file} onRemove={off || onRemoveAttachment === undefined ? undefined : () => { onRemoveAttachment(); area.current?.focus(); }} /></div>}
      <textarea
        ref={setArea}
        rows={1}
        value={value}
        placeholder={recording ? listeningPlaceholder : placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        {...textareaProps}
        className={cx(recording && "sk-ph-wrap", textareaProps?.className) || undefined}
      />
      <Wave hidden={!recording} source={waveLevelsSource} />
      {/* el clip: mientras se lee un documento espera (`aria-disabled`); con
          uno ya listo sigue vivo, y elegir otro sustituye al anterior */}
      {onAttach !== undefined && (
        <button
          type="button" className="sk-btn sk-btn--icon" aria-label="Adjuntar un documento"
          aria-disabled={off || reading || undefined}
          aria-busy={reading || undefined}
          aria-describedby={clipDescribedBy}
          tabIndex={off ? -1 : undefined}
          data-sk-tip={attachHint}
          onClick={off || reading ? undefined : onAttach}
        >
          <Icon name="clip" />
        </button>
      )}
      {attachHint !== undefined && onAttach !== undefined && <span className="sk-vh" id={attachHintId}>{attachHint}</span>}
      {/* el mando de voz NO se apaga con `disabled` (perdería el porqué), pero
          en stale sale del teclado como los demás: la clase sola es del ratón */}
      <button
        type="button" className={cx("sk-btn", "sk-btn--icon", busy && "sk-loading")} aria-label="Hablar en vez de escribir"
        {...(voiceTip === undefined && { "aria-pressed": listening === true && !busy && !opening })}
        aria-disabled={voiceTip !== undefined || off || busy || undefined}
        aria-busy={busy || opening || undefined}
        aria-describedby={describedBy}
        tabIndex={off ? -1 : undefined}
        data-sk-tip={voiceTip} data-sk-tip-pos={voiceTip !== undefined ? "bottom" : undefined}
        onClick={busy || off ? undefined : onListen}
      >
        {/* abriendo: spinner a talla de glifo (no `sk-loading`, que borra y
            apaga el mando): sigue pulsable, para cancelar */}
        {opening ? <span className="sk-spinner" aria-hidden="true" /> : <Icon name="mic" />}
      </button>
      {opening && <span className="sk-vh" id={hintId}>Pulsa para cancelar</span>}
      <button type="button" className={cx("sk-btn", "sk-btn--primary", "sk-btn--icon", sending === true && "sk-loading")} aria-label="Enviar" aria-busy={sending === true || undefined} aria-disabled={reading || undefined} aria-describedby={reading ? liveId : undefined} disabled={disabled} onClick={send}>
        <Icon name="send" />
      </button>
    </div>
  );
}
/**
 * Aviso SOLO para el lector de pantalla (`role=status`, oculto a la vista):
 * montado siempre, se rellena cuando pasa algo («Tu asesor ha respondido»).
 */
export function LiveNote({ children }: { children?: ReactNode }) {
  return <p className="sk-vh" role="status">{children}</p>;
}
export function ComposerNote({ stale, className, ...rest }: { stale?: boolean | undefined } & HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cx("sk-composer-note", stale && "sk-stale", className)} {...rest} />;
}

/** Gráfico dentro de la conversación: título, delta y la línea. */
export function Fig({ caption, delta, label, line, reference }: { caption: ReactNode; delta?: ReactNode | undefined; label: string; line: string; reference?: string | undefined }) {
  return (
    <div className="sk-fig">
      <div className="sk-fig-cap"><b>{caption}</b>{delta !== undefined && <span className="sk-fig-delta">{delta}</span>}</div>
      <svg viewBox="0 0 300 52" preserveAspectRatio="none" role="img" aria-label={label}>
        {reference !== undefined && <path className="sk-fig-ref" d={reference} />}
        <path className="sk-fig-line" d={line} />
      </svg>
    </div>
  );
}

/**
 * Fuente citada: de dónde sale lo dicho, y el detalle a demanda. Abierta,
 * la caja mide lo que su contenido: el tope de 14rem del sistema recortaba
 * sin aviso el último párrafo (tres cifras a 340 px ya no cabían). La
 * transición de `max-height` sigue siendo la del sistema; solo el destino
 * se mide. Cerrada, la caja no se lee ni se tabula (`aria-hidden` + `inert`:
 * la de «Sus fuentes» lleva enlaces, y no deben alcanzarse a ciegas).
 * Sin `act` es solo la línea (ADR-20: «criterio general…», sin caja). `sep`
 * es lo que une la línea al acto: « · » de ordinario; con «y N más», un
 * espacio, porque el acto continúa la lista.
 */
export function SrcLine({ children, act, detail, sep = " · " }: { children: ReactNode; act?: ReactNode | undefined; detail?: ReactNode | undefined; sep?: ReactNode | undefined }) {
  const [open, setOpen] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const id = useId();
  const [alto, setAlto] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = caja.current;
    if (!open || el === null) { setAlto(null); return; }
    const medir = (): void => setAlto(el.scrollHeight);
    medir();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(medir);
    if (ro !== null && el.firstElementChild !== null) ro.observe(el.firstElementChild);
    return () => ro?.disconnect();
  }, [open, detail]);
  if (act === undefined) return <div className="sk-srcline">{children}</div>;
  return (
    <div className="sk-srcline">
      {children}{sep}<button type="button" className="sk-btn sk-btn--link" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>{act}</button>
      <div ref={caja} id={id} aria-hidden={!open || undefined} inert={!open} className={cx("sk-srcbox", open && "sk-open")} style={open && alto !== null ? { maxHeight: alto } : undefined}><div>{detail}</div></div>
    </div>
  );
}

/**
 * «Sus fuentes» en pastillas (ADR-20 pt 5; showcase `#sus-fuentes`,
 * 2026-10-26): la fila siempre visible bajo cada respuesta, y debajo la
 * caja de siempre (`sk-srcbox`). Las pastillas que abren la caja
 * (`opensDetail`) y el acto final comparten su estado: todas dicen
 * `aria-expanded` a la vez. Abierta, la caja mide su contenido; cerrada,
 * es `inert` y `aria-hidden` (lleva enlaces), como la de `SrcLine`.
 */
interface SrcRowCtx { open: boolean; toggle: () => void; boxId: string | undefined }
const SrcRowContext = createContext<SrcRowCtx>({ open: false, toggle: () => {}, boxId: undefined });

export function SrcRow({ children, act, detail, label = "Sus fuentes" }: { children: ReactNode; act?: ReactNode | undefined; detail?: ReactNode | undefined; label?: string | undefined }) {
  const [open, setOpen] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const id = useId();
  const [alto, setAlto] = useState<number | null>(null);
  const conCaja = detail !== undefined;
  useLayoutEffect(() => {
    const el = caja.current;
    if (!open || el === null) { setAlto(null); return; }
    const medir = (): void => setAlto(el.scrollHeight);
    medir();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(medir);
    if (ro !== null && el.firstElementChild !== null) ro.observe(el.firstElementChild);
    return () => ro?.disconnect();
  }, [open, detail]);
  const ctx: SrcRowCtx = { open, toggle: () => setOpen((o) => !o), boxId: conCaja ? id : undefined };
  return (
    <SrcRowContext.Provider value={ctx}>
      <div className="sk-srcrow" role="group" aria-label={label}>
        {children}
        {conCaja && act !== undefined && (
          <button type="button" className="sk-btn sk-btn--link" aria-expanded={open} aria-controls={id} onClick={ctx.toggle}>{act}</button>
        )}
      </div>
      {conCaja && (
        <div ref={caja} id={id} aria-hidden={!open || undefined} inert={!open} className={cx("sk-srcbox", open && "sk-open")} style={open && alto !== null ? { maxHeight: alto } : undefined}><div>{detail}</div></div>
      )}
    </SrcRowContext.Provider>
  );
}

export type SrcPillTone = "method" | "signed" | "pending" | "data" | "down" | "plain";
export type SrcPillProps = {
  tone?: SrcPillTone | undefined;
  icon?: IconName | undefined;
  /** El nombre de la fuente, en tinta («Margen bruto»). */
  title?: ReactNode | undefined;
  /** La etiqueta, con las palabras del ADR («criterio de Skein, firmado el …»). */
  label?: ReactNode | undefined;
  /** Abre algo que no es la caja (la vista pública de una ficha): botón con `aria-haspopup=dialog`. */
  onClick?: ((e: MouseEvent<HTMLButtonElement>) => void) | undefined;
  /** Abre la caja de su fila (el método, las cifras, «y N más»). */
  opensDetail?: boolean | undefined;
};

/** Una pastilla de «Sus fuentes»: botón si abre algo; si no, texto. */
export function SrcPill({ tone = "plain", icon, title, label, onClick, opensDetail = false }: SrcPillProps) {
  const row = useContext(SrcRowContext);
  const className = cx("sk-srcpill", tone !== "plain" && `sk-srcpill--${tone}`);
  const inner = (
    <>
      {icon !== undefined && <Icon name={icon} />}
      {title !== undefined && <span className="sk-srcpill-t">{title}</span>}
      {title !== undefined && label !== undefined && <span className="sk-vh">, </span>}
      {label !== undefined && <span className="sk-srcpill-s">{label}</span>}
    </>
  );
  if (onClick !== undefined) return <button type="button" className={className} aria-haspopup="dialog" onClick={onClick}>{inner}</button>;
  if (opensDetail && row.boxId !== undefined) return <button type="button" className={className} aria-expanded={row.open} aria-controls={row.boxId} onClick={row.toggle}>{inner}</button>;
  return <span className={className}>{inner}</span>;
}

/**
 * Lo que cubre una fuente, en el flanco (showcase `#sus-fuentes`): el
 * resumen en un susurro, el desplegable «qué cubre» con la lista y las
 * desviaciones del catálogo en su nota, siempre visibles.
 */
/**
 * La lista de cobertura (`sk-cover-list`) SIEMPRE abierta: concepto → lo
 * que cubre. La de una hoja fijada en «Tus cifras» y «De dónde sale hoy
 * cada cifra»; dentro de `Cover`, la que se despliega.
 */
export function CoverList({ items, id, hidden, labelledBy }: {
  items: readonly { name: ReactNode; value: ReactNode }[];
  id?: string | undefined; hidden?: boolean | undefined; labelledBy?: string | undefined;
}) {
  return (
    <ul className="sk-cover-list" id={id} hidden={hidden} aria-labelledby={labelledBy}>
      {items.map((it, i) => <li key={i}><span className="sk-cover-nm">{it.name}</span><span className="sk-cover-p">{it.value}</span></li>)}
    </ul>
  );
}
export function Cover({ summary, items, notes = [], act = "qué cubre" }: {
  summary: ReactNode;
  items: readonly { name: ReactNode; value: ReactNode }[];
  notes?: readonly ReactNode[] | undefined;
  act?: ReactNode | undefined;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="sk-cover">
      <p className="sk-whisper">{summary}</p>
      {items.length > 0 && (
        <>
          <button type="button" className="sk-disclose" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>{act} <Icon name="chevron-r" /></button>
          <CoverList id={id} hidden={!open} items={items} />
        </>
      )}
      {notes.length > 0 && <div className="sk-cover-dev">{notes.map((n, i) => <p key={i}>{n}</p>)}</div>}
    </div>
  );
}

/** La jornada del asesor: hecho, ahora, previsto. */
export function Timeline({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("sk-timeline", className)} {...rest} />;
}
export function TlItem({ at, state = "done", children }: { at: ReactNode; state?: "done" | "now" | "next" | undefined; children: ReactNode }) {
  return <div className={cx("sk-tl-item", `sk-${state}`)}><span className="sk-tl-t">{at}</span>{children}</div>;
}

/** Lo que vigila por ti, con su regla y su línea. */
export function Watch({ title, rule, line, warn }: { title: ReactNode; rule: ReactNode; line?: string | undefined; warn?: boolean | undefined }) {
  return (
    <div className="sk-watch">
      <b>{title}</b><span className="sk-watch-rule">{rule}</span>
      {line !== undefined && (
        <svg viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true"><path className={warn === true ? "sk-warnline" : undefined} d={line} /></svg>
      )}
    </div>
  );
}

/**
 * Grupo plegable de la cortinilla «Lo que sé». `hot` = el contador de lo que
 * espera respuesta (la puerta del sí fuera del chat); `note`, la glosa de
 * alcance del grupo («· solo lo ves tú»).
 *
 * `id` hace del grupo un DESTINO: la cola del asesor abre la cortinilla en
 * el grupo que toca (`/lo-que-se#por-confirmar`). La cabecera lleva
 * `${id}-head` para que la página pueda darle el foco sin buscar por clase.
 */
export function KGroup({ id, title, count, hot, note, open, onToggle, hidden, children }: {
  id?: string | undefined; title: ReactNode; count?: ReactNode | undefined; hot?: boolean | undefined; note?: ReactNode | undefined;
  open: boolean; onToggle: () => void; hidden?: boolean | undefined; children: ReactNode;
}) {
  return (
    <div className={cx("sk-kgroup", open && "sk-open")} id={id} hidden={hidden}>
      <button type="button" className="sk-kghead" id={id === undefined ? undefined : `${id}-head`} aria-expanded={open} onClick={onToggle}>
        {title} {count !== undefined && (hot === true ? <Count hot>{count}</Count> : <span className="sk-fainted">· {count}</span>)}
        {note !== undefined && <span className="sk-kgnote">{note}</span>}
        <Icon name="chevron-r" className="sk-chevron" />
      </button>
      <div className="sk-kgbody"><div>{children}</div></div>
    </div>
  );
}
/**
 * Ficha de memoria: lo que sabe, desde cuándo y cómo lo supo. `acts` son los
 * actos de la ficha (corregir, retirar…) y `form` el formulario que se abre
 * en sitio; mientras está abierto, `editing` apaga los actos (no los borra).
 */
export function KItem({ when, faint, acts, form, editing, check, children }: {
  when?: ReactNode | undefined; faint?: boolean | undefined; acts?: ReactNode | undefined;
  form?: ReactNode | undefined; editing?: boolean | undefined;
  /** CASILLA DE FICHA (`sk-kitem--check`, 2026-11-03): la ficha empieza por una `Check`;
      su etiqueta va a la talla de la prosa y los actos se sangran bajo el texto. */
  check?: boolean | undefined; children: ReactNode;
}) {
  return (
    <div className={cx("sk-kitem", check === true && "sk-kitem--check", faint && "sk-fainted", editing === true && "sk-editing")} style={faint === true ? { fontStyle: "italic" } : undefined}>
      {children}
      {acts !== undefined && <span className="sk-kitem-acts">{acts}</span>}
      {when !== undefined && <span className="sk-k-when">{when}</span>}
      {form}
    </div>
  );
}
/** Lo que se abre DENTRO de una ficha: corregir en sitio, o la pregunta de retirar. */
export function KItemForm({ children }: { children: ReactNode }) {
  return <div className="sk-kitem-form">{children}</div>;
}
/** Fila de actos dentro del formulario de una ficha. */
export function KItemActs({ children }: { children: ReactNode }) {
  return <div className="sk-kitem-acts">{children}</div>;
}

/** Cortinilla derecha con velo (la memoria a demanda). */
/**
 * Cortinilla derecha con velo (la memoria a demanda). Teclado completo, como
 * la canónica: el foco entra al abrir, Escape cierra, Tab no se escapa del
 * diálogo y el foco vuelve al mando que lo abrió. CERRADA = `inert`: fuera
 * del orden de tabulación y del árbol de accesibilidad — trasladarla fuera de
 * pantalla no basta (crítico del auditor, 2026-09-15).
 */
export function Drawer({ open, onClose, id, label, labelledBy, returnFocus, read, children }: { open: boolean; onClose: () => void; id: string; label?: string | undefined; labelledBy?: string | undefined; returnFocus?: string | undefined; /** Cortinilla de LECTURA (`sk-drawer--read`): más ancha en pantallas grandes. */ read?: boolean | undefined; children: ReactNode }) {
  const caja = useRef<HTMLElement>(null);
  const cerrar = useRef(onClose);
  cerrar.current = onClose;
  useEffect(() => {
    if (!open) return;
    const antes = document.activeElement as HTMLElement | null;
    // el filtro mira TAMBIÉN el tabindex: en error el «último» del ciclo era
    // un mando apagado que nunca recibe foco, y el Tab se escapaba del
    // diálogo (crítico ya cerrado en la canónica de la cuenta)
    focables(caja.current)[0]?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        // dentro de un campo (el buscador), Escape es del campo
        const t = e.target;
        if (t instanceof Element && t.closest("input,textarea") !== null) return;
        e.preventDefault(); cerrar.current(); return;
      }
      if (e.key !== "Tab") return;
      const f = focables(caja.current);
      const primero = f[0], ultimo = f[f.length - 1];
      if (primero === undefined || ultimo === undefined) return;
      if (caja.current?.contains(document.activeElement) !== true) { e.preventDefault(); primero.focus(); }
      else if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); devolverFoco(antes, returnFocus); };
  }, [open, returnFocus]);
  return (
    <>
      <div className={cx("sk-scrim", open && "sk-open")} onClick={onClose} />
      <aside ref={caja} className={cx("sk-drawer", "sk-drawer--right", read === true && "sk-drawer--read", open && "sk-open")} id={id} role="dialog" aria-modal="true" aria-label={labelledBy === undefined ? label : undefined} aria-labelledby={labelledBy} inert={!open}>
        {children}
      </aside>
    </>
  );
}
/**
 * Cabecera de la cortinilla: título + cierre (hueco anotado del sistema).
 * `titleId` nombra el diálogo (`Drawer labelledBy`); `closeLabel` dice qué
 * se cierra cuando «Cerrar» no basta («Cerrar la ficha»).
 */
export function DrawerHead({ title, onClose, titleId, closeLabel = "Cerrar", style }: { title: ReactNode; onClose: () => void; titleId?: string | undefined; closeLabel?: string | undefined; style?: CSSProperties | undefined }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: "var(--sk-sp-4)", ...style }}>
      <h2 className="sk-h3" id={titleId}>{title}</h2>
      <button type="button" className="sk-btn sk-btn--icon sk-btn--s" style={{ marginLeft: "auto" }} aria-label={closeLabel} onClick={onClose}>✕</button>
    </div>
  );
}

export interface Step { label: ReactNode; state: "pending" | "doing" | "done" }
/** Pasos guiados apilados, con la fase en curso girando. */
export function StepsStack({ steps, style }: { steps: ReadonlyArray<Step>; style?: CSSProperties | undefined }) {
  return (
    <div className="sk-steps sk-steps--stack" style={style}>
      {steps.map((s, i) => (
        <span key={i} className={cx("sk-step", s.state === "doing" && "sk-doing", s.state === "done" && "sk-done")}>
          <span className="sk-step-n">{s.state === "done" ? "" : i + 1}</span>{s.label}
        </span>
      ))}
    </div>
  );
}

/** Esqueleto de un turno mientras el asesor recuerda dónde lo dejasteis. */
export function TurnSkeleton({ widths, bubble, head = "22%" }: { widths: ReadonlyArray<string>; bubble?: boolean | undefined; head?: string | undefined }) {
  const key = useId();
  return (
    <div className="sk-turn">
      <Skeleton style={{ height: ".8em", width: head, marginBottom: "var(--sk-sp-4)" }} />
      {bubble === true
        ? <Skeleton style={{ height: "2.6em", width: "70%", borderRadius: "var(--sk-r-l)" }} />
        : widths.map((w, i) => <Skeleton key={`${key}-${i}`} style={{ height: "1em", width: w, marginBottom: i === widths.length - 1 ? undefined : "var(--sk-sp-3)" }} />)}
    </div>
  );
}
